import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { ogImageUrl } from "../lib/ogImages";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Clock, ArrowLeft, ArrowUpRight, Twitter, Linkedin, Facebook, Link as LinkIcon, MessageCircle } from "lucide-react";
import HrefLangTags from "../components/HrefLangTags";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import { useLanguage } from "../contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdSlot() { return null; /* Phase 4: AdSense removed in favor of editorial UX. */ }

function ShareBar({ url, title, lang = "tr" }) {
  const enc = encodeURIComponent;
  const shareLabel = { tr: "Paylaş", en: "Share", es: "Compartir", de: "Teilen", fr: "Partager", ar: "شارك" };
  const links = [
    { name: "X", icon: Twitter, href: `https://x.com/intent/tweet?text=${enc(title)}&url=${enc(url)}` },
    { name: "LinkedIn", icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}` },
    { name: "Facebook", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}` },
    { name: "WhatsApp", icon: MessageCircle, href: `https://wa.me/?text=${enc(title + " " + url)}` },
  ];
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); alert(lang === "tr" ? "Link kopyalandı" : "Link copied"); } catch {}
  };
  return (
    <div className="flex items-center gap-2 mt-8 pt-8 border-t border-mist" data-testid="share-bar">
      <span className="text-xs uppercase tracking-[0.18em] text-ink/55 font-bold me-2">{shareLabel[lang] || shareLabel.en}</span>
      {links.map((l) => (
        <a key={l.name} href={l.href} target="_blank" rel="noopener noreferrer"
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-mist text-ink/60 hover:text-ink hover:border-brand-pink transition-colors">
          <l.icon className="h-4 w-4" />
        </a>
      ))}
      <button onClick={copy} className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-mist text-ink/60 hover:text-ink hover:border-brand-pink transition-colors">
        <LinkIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function BlogArticlePage() {
  const { lang } = useLanguage();
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [progress, setProgress] = useState(0);

  // i18n helpers
  const i = (tr, en, es, de, fr, ar) => {
    if (lang === "tr") return tr;
    if (lang === "en") return en;
    if (lang === "es") return es;
    if (lang === "de") return de;
    if (lang === "fr") return fr;
    if (lang === "ar") return ar;
    return en;
  };

  useEffect(() => {
    setLoading(true); setErr("");
    axios.get(`${API}/blog/articles/${slug}`, { params: { lang } })
      .then((r) => setArticle(r.data))
      .catch((e) => setErr(e?.response?.status === 404 ? "Yazı bulunamadı." : "Yazı yüklenemedi."))
      .finally(() => setLoading(false));
    axios.get(`${API}/blog/related/${slug}`, { params: { lang, limit: 3 } })
      .then((r) => setRelated(r.data || [])).catch(() => {});
  }, [slug, lang]);

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const scrolled = h.scrollTop / (h.scrollHeight - h.clientHeight) || 0;
      setProgress(Math.min(100, Math.max(0, scrolled * 100)));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [article]);

  if (loading) {
    return <div className="min-h-screen bg-cream"><Header /><div className="container-page py-section text-center text-ink/55">Yükleniyor…</div><Footer /></div>;
  }
  if (err || !article) {
    return (
      <div className="min-h-screen bg-cream">
        <Header />
        <div className="container-page py-section sm:py-section-lg text-center">
          <h1 className="font-display text-display-2 font-bold text-ink">404</h1>
          <p className="mt-3 text-ink/65">{err || "Yazı bulunamadı."}</p>
          <Link to={`/${lang}/blog`} className="mt-6 inline-flex items-center gap-2 text-brand-pink-deep hover:underline">
            <ArrowLeft className="h-4 w-4" /> Blog'a dön
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const tr = article.translations || {};
  const url = typeof window !== "undefined" ? window.location.href : "";
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": tr.title,
    "description": tr.seo_description || tr.excerpt,
    "image": article.cover_image_url || undefined,
    "datePublished": article.published_at,
    "dateModified": article.updated_at || article.published_at,
    "author": { "@type": "Organization", "name": "Yastık Medya" },
    "publisher": { "@type": "Organization", "name": "Yastık Medya" },
    "mainEntityOfPage": url,
  };
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", position: 1, name: "Home", item: `${BACKEND_URL}/${lang}/` },
      { "@type": "ListItem", position: 2, name: "Blog", item: `${BACKEND_URL}/${lang}/blog` },
      { "@type": "ListItem", position: 3, name: tr.title, item: url },
    ],
  };

  // Smart content renderer: detects HTML vs Markdown
  const content = tr.content || "";
  const isHTML = content.trimStart().startsWith("<");
  
  // For markdown: split at 3rd h2 for ad injection
  let segments = ["", ""];
  if (!isHTML) {
    let h2Count = 0;
    const result = content.split("\n").reduce((acc, line) => {
      if (/^##\s+/.test(line)) h2Count += 1;
      if (h2Count === 3 && !acc._injected && /^##\s+/.test(line)) {
        acc.parts.push(acc.buf.join("\n"));
        acc.buf = [];
        acc._injected = true;
        acc.buf.push(line);
      } else {
        acc.buf.push(line);
      }
      return acc;
    }, { parts: [], buf: [], _injected: false });
    result.parts.push(result.buf.join("\n"));
    segments = result.parts;
  }

  return (
    <div data-testid="blog-article-page" className="min-h-screen bg-cream text-ink">
      <Helmet>
        <title>{`${tr.seo_title || tr.title || "Blog"} · Yastık Medya`}</title>
        <meta name="description" content={tr.seo_description || tr.excerpt || ""} />
        <link rel="canonical" href={`https://yastikmedya.com/${lang}/blog/${slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://yastikmedya.com/${lang}/blog/${slug}`} />
        <meta property="og:title" content={`${tr.title || ""} — Yastık Medya`} />
        <meta property="og:description" content={tr.seo_description || tr.excerpt || ""} />
        <meta property="og:image" content={article.cover_image_url || ogImageUrl("blog")} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Yastık Medya" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${tr.title || ""} — Yastık Medya`} />
        <meta name="twitter:description" content={tr.seo_description || tr.excerpt || ""} />
        <meta name="twitter:image" content={article.cover_image_url || ogImageUrl("blog")} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
      </Helmet>
      <HrefLangTags pathSuffix={`blog/${slug}`} />

      {/* Reading progress bar */}
      <div data-testid="reading-progress" className="fixed top-0 inset-x-0 h-1 z-40 pointer-events-none">
        <div className="h-full bg-brand-pink transition-[width] duration-100" style={{ width: `${progress}%` }} />
      </div>

      <Header />

      <article>
        {/* Hero */}
        <header className="bg-cream border-b border-mist">
          <div className="container-page py-section">
            <Link to={`/${lang}/blog`} className="inline-flex items-center gap-2 text-sm text-ink/60 hover:text-ink mb-8">
              <ArrowLeft className="h-4 w-4" /> {i("Tüm yazılar","All posts","Todos los artículos","Alle Beiträge","Tous les articles","كل المقالات")}
            </Link>
            <span className="text-xs uppercase tracking-[0.2em] text-brand-pink-deep font-bold">{article.category}</span>
            <h1 className="mt-4 font-display text-display-1 font-bold leading-[1.0] text-ink max-w-4xl">
              {tr.title}
            </h1>
            <p className="mt-6 text-body-lg text-ink/65 leading-relaxed max-w-2xl">{tr.excerpt}</p>
            <div className="mt-7 flex items-center gap-5 text-sm text-ink/55">
              <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" />{article.reading_time_min || 8} {i("dk okuma","min read","min lectura","Min. Lesen","min de lecture","دقائق قراءة")}</span>
              {article.published_at ? <span>{new Date(article.published_at).toLocaleDateString("tr-TR", { dateStyle: "long" })}</span> : null}
            </div>
          </div>
          {article.cover_image_url ? (
            <div className="aspect-[16/8] sm:aspect-[16/7] max-h-[640px] overflow-hidden">
              <img src={article.cover_image_url} alt={tr.title} className="h-full w-full object-cover" />
            </div>
          ) : null}
        </header>

        {/* Body */}
        <div className="container-page py-section lg:grid lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-8">
            <AdSlot slot="article-top" />
            
            {/* Smart renderer: HTML or Markdown */}
            {isHTML ? (
              <Reveal as="div" className="prose prose-pink max-w-none
                prose-headings:font-display prose-headings:font-bold prose-headings:text-ink
                prose-h1:text-display-2 prose-h1:mt-10 prose-h1:mb-5
                prose-h2:text-h1 prose-h2:mt-12 prose-h2:mb-4
                prose-h3:text-h2 prose-h3:mt-8 prose-h3:mb-3
                prose-p:text-ink/80 prose-p:leading-relaxed prose-p:my-4
                prose-strong:text-ink prose-strong:font-bold
                prose-a:text-brand-pink-deep prose-a:no-underline hover:prose-a:underline
                prose-ul:my-4 prose-li:text-ink/80 prose-li:my-1
                prose-blockquote:border-s-4 prose-blockquote:border-brand-pink prose-blockquote:bg-brand-pink/5 prose-blockquote:rounded-e-xl prose-blockquote:py-1 prose-blockquote:px-5">
                <div dangerouslySetInnerHTML={{ __html: content }} />
              </Reveal>
            ) : (
              <Reveal as="div" className="prose prose-pink max-w-none
                prose-headings:font-display prose-headings:font-bold prose-headings:text-ink
                prose-h2:text-h1 prose-h2:mt-12 prose-h2:mb-4
                prose-h3:text-h2 prose-h3:mt-8 prose-h3:mb-3
                prose-p:text-ink/80 prose-p:leading-relaxed
                prose-strong:text-ink prose-strong:font-bold
                prose-a:text-brand-pink-deep prose-a:no-underline hover:prose-a:underline
                prose-blockquote:border-s-4 prose-blockquote:border-brand-pink prose-blockquote:bg-brand-pink/5 prose-blockquote:rounded-e-xl prose-blockquote:py-1 prose-blockquote:px-5 prose-blockquote:not-italic
                prose-ul:my-4 prose-li:text-ink/80
                prose-table:text-sm prose-th:bg-mist/40 prose-th:text-ink">
                {segments[0] ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{segments[0]}</ReactMarkdown> : null}
              </Reveal>
            )}

            {segments[1] ? <AdSlot slot="article-mid" /> : null}
            {!isHTML && segments[1] ? (
              <div className="prose prose-pink max-w-none
                prose-headings:font-display prose-headings:font-bold prose-headings:text-ink
                prose-h2:text-h1 prose-h2:mt-12 prose-h2:mb-4
                prose-h3:text-h2 prose-h3:mt-8 prose-h3:mb-3
                prose-p:text-ink/80 prose-p:leading-relaxed
                prose-strong:text-ink prose-strong:font-bold
                prose-a:text-brand-pink-deep prose-a:no-underline hover:prose-a:underline
                prose-blockquote:border-s-4 prose-blockquote:border-brand-pink prose-blockquote:bg-brand-pink/5 prose-blockquote:rounded-e-xl prose-blockquote:py-1 prose-blockquote:px-5 prose-blockquote:not-italic
                prose-ul:my-4 prose-li:text-ink/80
                prose-table:text-sm prose-th:bg-mist/40 prose-th:text-ink">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{segments[1]}</ReactMarkdown>
              </div>
            ) : null}

            <AdSlot slot="article-bottom" />

            {/* Author bio */}
            <div className="mt-8 rounded-3xl bg-gradient-to-br from-cream to-mist/50 border border-mist p-7 sm:p-8 flex flex-col sm:flex-row gap-5 items-start">
              <div className="h-16 w-16 rounded-2xl flex items-center justify-center text-cream font-display text-2xl font-black flex-shrink-0"
                style={{ background: "linear-gradient(135deg, #EE4F7A 0%, #8B3D9E 50%, #55D9C2 100%)" }}>
                YM
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-brand-pink-deep font-bold">{i("Yazar","Author","Autor","Autor","Auteur","المؤلف")}</p>
                <h3 className="mt-1 font-display text-h2 font-bold text-ink">{i("Yastık Medya Ekibi","Yastık Medya Team","Equipo de Yastık Medya","Yastık Medya Team","Équipe Yastık Medya","فريق يسطق ميديا")}</h3>
                <p className="mt-2 text-sm text-ink/70 leading-relaxed">
                  {i(
                    "Performans, kreatif ve global SEO'da 5+ yıllık tecrübe. KOBİ'den marka grubuna 200+ projede ölçülebilir büyüme.",
                    "5+ years of expertise in performance, creative and global SEO. Measurable growth in 200+ projects from SMBs to brand groups.",
                    "Más de 5 años de experiencia en rendimiento, creativo y SEO global. Crecimiento medible en más de 200 proyectos.",
                    "5+ Jahre Erfahrung in Performance, Kreativ und globalem SEO. Messbares Wachstum in 200+ Projekten.",
                    "Plus de 5 ans d'expertise en performance, créatif et SEO mondial. Croissance mesurable dans 200+ projets.",
                    "أكثر من 5 سنوات خبرة في الأداء والإبداع وتحسين محركات البحث العالمية."
                  )}
                </p>
              </div>
            </div>

            <ShareBar url={url} title={tr.title} lang={lang} />
          </div>

          {/* Sidebar (desktop only) */}
          <aside className="hidden lg:block lg:col-span-4 space-y-6">
            <AdSlot slot="sidebar-1" />
            <div className="rounded-3xl bg-ink text-cream p-6 sticky top-24">
              <p className="text-xs uppercase tracking-[0.2em] text-brand-pink font-bold">Hazır mısın?</p>
              <h3 className="mt-3 font-display text-h2 font-bold leading-tight">
                Markanı bir sonraki seviyeye taşıyalım.
              </h3>
              <Link to={`/${lang}/contact`} className="mt-5 inline-flex items-center gap-2 rounded-full bg-brand-pink px-5 py-2.5 text-sm font-semibold hover:bg-brand-pink-deep transition-colors">
                İletişime geç <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </aside>
        </div>

        {/* Related */}
        {related.length > 0 ? (
          <section className="bg-cream border-t border-mist py-section">
            <div className="container-page">
              <h2 className="font-display text-h1 font-bold text-ink">İlgili yazılar</h2>
              <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((r) => {
                  const rt = r.translations || {};
                  return (
                    <Link key={r.id || r.slug} to={`/${lang}/blog/${r.slug}`}
                      data-testid={`related-${r.slug}`}
                      className="group rounded-3xl bg-white border border-mist p-6 hover:border-brand-pink/60 transition-colors">
                      <span className="text-xs uppercase tracking-[0.18em] text-brand-pink-deep font-bold">{r.category}</span>
                      <h3 className="mt-3 font-display text-h2 font-semibold text-ink line-clamp-2">{rt.title}</h3>
                      <p className="mt-2 text-sm text-ink/65 line-clamp-3">{rt.excerpt}</p>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        ) : null}
      </article>

      <Footer />
    </div>
  );
}
